<script>
	import "@unocss/reset/tailwind.css";
	import "virtual:uno.css";
	import "$lib/global.css";
	import Nav from "$lib/components/Nav.svelte";
	import { onMount, setContext } from "svelte";
	import { selectedPlan } from "$lib/store";
	import UtilityBar from "$lib/components/UtilityBar.svelte";
	import ArModeSelector from "$lib/components/ARModeSelector.svelte";
	import Spinner from "$lib/components/Spinner.svelte";
	import { ensureLatestVersion } from "$lib/utils";
	import { writable } from "svelte/store";
	import nativeRequest from "$lib/nativeRequest";
	import { navigating, page } from "$app/stores";

	/** @type {import('./$types').PageData} */
	export let data;

	let startTime = Date.now();

	const safeArea = data.safeArea?.split(",").map((v) => Math.floor(parseFloat(v))) || [0, 0];

	/** @type {DeviceInfo} */
	const deviceInfo = {
		isWebView: data.isWebView,
		deviceId: data.deviceId,
		device: data.device,
		anonUserId: data.anonUserId,
	};
	setContext("deviceInfo", deviceInfo);

	setContext("user", data.user);

	/** @type {import("svelte/store").Writable<ModelCache>} */
	const modelCache = writable({ plans: [] });
	setContext("modelCache", modelCache);

	onMount(() => {
		console.log("Powered by Showhomes.io https://showhomes.io");

		document.body.style.setProperty(
			"--safe-area-top",
			`${safeArea[0] / window.devicePixelRatio}px`,
		);
		document.body.style.setProperty(
			"--safe-area-bottom",
			`${safeArea[1] / window.devicePixelRatio}px`,
		);

		if (deviceInfo.isWebView) {
			const doSetUser = () =>
				nativeRequest("set_user", {
					id: data.user?.id || data.anonUserId,
					email: data.user?.email,
					anonUserId: data.anonUserId,
				});
			if ($page.url.searchParams.has("logout", "true")) {
				console.log("Performing native logout");
				nativeRequest("a_reset", { keepAnonUserId: true }).then(doSetUser);
			} else {
				doSetUser();
			}

			updateModelCache();
			document.addEventListener("arSessionEnd", updateModelCache);
		}

		ensureLatestVersion();

		if (data.authTokenExpired) {
			alert("Session expired. You have been logged out.");
		}

		return () => {
			if (deviceInfo.isWebView) {
				document.removeEventListener("arSessionEnd", updateModelCache);
			}
		};
	});

	function onVisibilityChanged() {
		if (document.visibilityState === "visible") {
			if (Date.now() - startTime > 24 * 60 * 60 * 1000) {
				console.log("Page reloaded due to being in background for more than 24 hours.");
				window.location.reload();
			} else {
				ensureLatestVersion();
			}
		}
	}

	async function updateModelCache() {
		const c = await nativeRequest("cache_request");
		$modelCache = c;
		console.log("Updated model cache");
	}
</script>

<svelte:document on:visibilitychange={onVisibilityChanged} />

<!-- Top safe area padding -->
<div class="h-$safe-area-top" />
<div class="fixed left-0 top-0 bg-primary w-screen h-$safe-area-top z-200" />

<!-- Header -->
<Nav />
<UtilityBar />

<!-- Main -->
<main class="p-4">
	{#if !!$navigating && $navigating.type !== "leave"}
		<!-- Loading overlay -->
		<div
			class="w-full h-full flex flex-col justify-center items-center text-black z-1000"
			style="--spinner-color: black"
		>
			<Spinner />
			<div>Loading...</div>
		</div>
	{:else}
		<slot />
	{/if}
</main>

<!-- Bottom safe area padding -->
<div class="h-$safe-area-bottom" />
<div class="fixed left-0 bottom-0 bg-primary w-screen h-$safe-area-bottom z-200" />

<!-- AR selector overlay -->
{#if $selectedPlan && deviceInfo.isWebView}
	<ArModeSelector plan={$selectedPlan} on:cancel={() => ($selectedPlan = null)} />
{/if}
